import * as React from "react";
import {
    Box,
    Card,
    CardContent,
    Divider,
    List,
    ListItem,
    ListItemText,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {CervinoHeaderText} from "../typography/CervinoHeaderText";
import CervinoText from "../typography/CervinoText";

export function FlippingCard({
                                 id,
                                 openId,
                                 setOpenId,
                                 title,
                                 imageUrl,
                                 ingredients = [],
                                 steps = [],
                                 height = "100%",
                             }) {
    const theme = useTheme();
    const isLgUp = useMediaQuery(theme.breakpoints.up("lg"));
    const canHover = useMediaQuery("(hover: hover) and (pointer: fine)");
    const hoverFlipEnabled = isLgUp && canHover;

    const [isHovered, setIsHovered] = React.useState(false);

    const resolvedImageUrl =
        typeof imageUrl === "string" ? imageUrl : imageUrl?.default || imageUrl?.src || "";

    const isOpenMobile = openId === id;
    const isFlipped = hoverFlipEnabled ? isHovered : isOpenMobile;

    const handleClick = () => {
        if (hoverFlipEnabled) return; // lg/xl uses hover only
        setOpenId?.((prev) => (prev === id ? null : id)); // ✅ only one open at a time
    };

    return (
        <Box
            onMouseEnter={hoverFlipEnabled ? () => setIsHovered(true) : undefined}
            onMouseLeave={hoverFlipEnabled ? () => setIsHovered(false) : undefined}
            onClick={handleClick}
            role={!hoverFlipEnabled ? "button" : undefined}
            tabIndex={!hoverFlipEnabled ? 0 : -1}
            onKeyDown={(e) => {
                if (!hoverFlipEnabled && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    handleClick();
                }
            }}
            sx={{
                width: "100%",
                height,
                perspective: "1400px",
                position: "relative",
                cursor: hoverFlipEnabled ? "default" : "pointer",
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    height: "100%",
                    transformStyle: "preserve-3d",
                    WebkitTransformStyle: "preserve-3d",
                    willChange: "transform",
                    transition: "transform 820ms cubic-bezier(.2,.8,.2,1)",
                    transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
                }}
            >
                {/* FRONT */}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        backfaceVisibility: "hidden",
                        WebkitBackfaceVisibility: "hidden",
                        transform: "rotateY(0deg) translateZ(1px)",
                        pointerEvents: "none",
                    }}
                >
                    <Card
                        sx={{
                            height: "100%",
                            borderRadius: 4,
                            overflow: "hidden",
                            border: "1px solid",
                            borderColor: "divider",
                            boxShadow: "0 10px 30px rgba(0,0,0,0.10), 0 2px 10px rgba(0,0,0,0.08)",
                            position: "relative",
                        }}
                    >
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                backgroundImage: resolvedImageUrl ? `url(${resolvedImageUrl})` : "none",
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                transform: "translateZ(0)",
                            }}
                        />
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                            }}
                        />
                        <CardContent
                            sx={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                right: 0,
                                textAlign: "left",
                                color: "common.white",
                                background: "linear-gradient(180deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0.25) 80%, rgba(0,0,0,0) 100%)",
                            }}
                        >
                            <CervinoHeaderText
                                sx={{
                                    fontSize: { xs: 24, sm: 30, md: 34, lg: 40, xl: 40 },
                                    textAlign: "left",
                                }}
                            >
                                {title.toUpperCase()}
                            </CervinoHeaderText>
                        </CardContent>
                    </Card>
                </Box>

                {/* BACK */}
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        backfaceVisibility: "hidden",
                        WebkitBackfaceVisibility: "hidden",
                        transform: "rotateY(180deg) translateZ(1px)",
                        pointerEvents: "none",
                    }}
                >
                    <Card
                        sx={{
                            height: "100%",
                            borderRadius: 4,
                            overflow: "hidden",
                            border: "1px solid",
                            borderColor: "divider",
                            boxShadow: "0 10px 30px rgba(0,0,0,0.10), 0 2px 10px rgba(0,0,0,0.08)",
                            position: "relative",
                        }}
                    >
                        {/* Background image */}
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                backgroundImage: resolvedImageUrl ? `url(${resolvedImageUrl})` : "none",
                                backgroundSize: "cover",
                                backgroundPosition: "center",
                                filter: "saturate(0.95) contrast(1.05)",
                                transform: "translateZ(0)",
                            }}
                        />

                        {/* Dark overlay (keeps image visible, improves contrast) */}
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                background:
                                    "linear-gradient(180deg, rgba(0,0,0,0.72) 0%, rgba(0,0,0,0.62) 45%, rgba(0,0,0,0.55) 100%)",
                            }}
                        />

                        {/* Optional: subtle vignette for extra readability */}
                        <Box
                            sx={{
                                position: "absolute",
                                inset: 0,
                                background:
                                    "radial-gradient(80% 60% at 50% 30%, rgba(255,255,255,0.06) 0%, rgba(0,0,0,0) 60%)",
                                mixBlendMode: "overlay",
                                pointerEvents: "none",
                            }}
                        />

                        <CardContent
                            sx={{
                                position: "relative",
                                height: "100%",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                justifyContent: "flex-start",
                                textAlign: "left",
                                color: "common.white",
                                paddingX: 1.5,
                                paddingTop: 1.5
                            }}
                        >
                            {isLgUp && (
                                <>
                                    <CervinoHeaderText
                                        align="left"
                                        sx={{
                                            fontSize: { lg: 24, xl: 26 },
                                            lineHeight: 1,
                                            width: "100%",
                                            textAlign: "left",
                                            mb: 1,
                                        }}
                                    >
                                        {title.toUpperCase()}
                                    </CervinoHeaderText>

                                    <Divider
                                        sx={{
                                            width: "100%",
                                            my: 1,
                                            borderColor: "common.white",
                                        }}
                                    />
                                </>
                            )}

                            {/* Scroll area */}
                            <Box
                                sx={{
                                    width: "100%",
                                    overflow: "auto",
                                }}
                            >
                                {/* Ingredients */}
                                <CervinoText
                                    sx={{
                                        fontWeight: 700,
                                        lineHeight: 1,
                                        fontSize: { xs: 14, sm: 15, md: 18, lg: 18, xl: 18 },
                                        textAlign: "left",
                                    }}
                                >
                                    INGREDIENTS
                                </CervinoText>

                                <List
                                    dense
                                    sx={{
                                        py: 0,
                                        listStyleType: "disc",
                                        pl: {xs: 2, sm: 2, md: 2, lg: 3, xl: 3},
                                        width: "100%",
                                    }}
                                >
                                    {ingredients.map((item, idx) => (
                                        <ListItem
                                            key={`${item}-${idx}`}
                                            sx={{
                                                display: "list-item",
                                                py: 0,
                                                width: "100%",
                                                fontSize: { xs: 11, sm: 11, md: 14, lg: 14, xl: 14 },
                                            }}
                                            disableGutters
                                        >
                                            <ListItemText
                                                primary={item}
                                                slotProps={{
                                                    primary: {
                                                        sx: {
                                                            fontSize: { xs: 13, sm: 14, md: 16, lg: 16, xl: 16 },
                                                            lineHeight: {xs: 1.1, sm: 1.1, md: 1.1, lg: 1.1, xl: 1.1},
                                                            color: "common.white",
                                                            textAlign: "left",
                                                        },
                                                    },
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>

                                <Divider
                                    sx={{
                                        width: "100%",
                                        my: 1,
                                        borderColor: "common.white",
                                    }}
                                />

                                {/* Instructions */}
                                <CervinoText
                                    sx={{
                                        fontWeight: 700,
                                        fontSize: { xs: 14, sm: 15, md: 18, lg: 18, xl: 18 },
                                        textAlign: "left",
                                    }}
                                >
                                    INSTRUCTIONS
                                </CervinoText>

                                <List
                                    dense
                                    sx={{
                                        py: 0,
                                        listStyleType: "decimal",
                                        pl: {xs: 2, sm: 2, md: 2, lg: 3, xl: 3},
                                        width: "100%",
                                    }}
                                >
                                    {steps.map((step, idx) => (
                                        <ListItem
                                            key={`${idx}-${step}`}
                                            sx={{
                                                display: "list-item",
                                                py: 0,
                                                width: "100%",
                                                fontSize: { xs: 11, sm: 11, md: 14, lg: 14, xl: 14 },
                                            }}
                                            disableGutters
                                        >
                                            <ListItemText
                                                primary={step}
                                                slotProps={{
                                                    primary: {
                                                        sx: {
                                                            fontSize: { xs: 13, sm: 14, md: 16, lg: 16, xl: 16 },
                                                            lineHeight: {xs: 1.1, sm: 1.1, md: 1.1, lg: 1.1, xl: 1.1},
                                                            color: "common.white",
                                                            textAlign: "left",
                                                        },
                                                    },
                                                }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        </CardContent>
                    </Card>
                </Box>

            </Box>
        </Box>
    );
}
